import { APP_SETTINGS } from '../config/appSettings'
import type { AccountAccess } from './accountAccess'

/**
 * Plan limits as the app uses them. The numbers come only from the server's plan catalog
 * (`account_types.capacities`, read through `get_my_account_access`); the only copy here is the guest's (appSettings `guestPlan`,
 * a hand-kept mirror of the Free row). Changing a limit: docs/PLAN_LIMITS.md has the checklist.
 * The server is authoritative. These helpers let the app say no early, with a useful message, and keep
 * the person's work saved on the device either way.
 */
export type PlanLimitKind = 'images'

export class PlanLimitError extends Error {
  constructor(
    readonly kind: PlanLimitKind,
    readonly limit: number,
    readonly usage: number,
    /** For a restore: how many images the account is over by. */
    readonly over?: number,
  ) {
    super(planLimitMessage(kind, limit, over))
    this.name = 'PlanLimitError'
  }
}

export function planLimitMessage(kind: PlanLimitKind, limit: number, over?: number): string {
  switch (kind) {
    case 'images':
      return over && over > 0
        ? `Restoring this note needs room for ${over.toLocaleString()} more image${over === 1 ? '' : 's'}. Delete an image or a note with images first. Your limit is ${limit.toLocaleString()} images.`
        : `You have reached your limit of ${limit.toLocaleString()} images. Delete an image or a note with images to add more.`
  }
}

const SERVER_CODES: Record<string, PlanLimitKind> = {
  IMAGE_LIMIT_REACHED: 'images',
}

/** Maps the server's typed refusal (stable message, JSON detail) to a PlanLimitError, or null. */
export function parsePlanLimitError(error: { message?: string; details?: string }): PlanLimitError | null {
  const kind = SERVER_CODES[error.message ?? '']
  if (!kind) return null
  let limit = 0
  let usage = 0
  try {
    const details = JSON.parse(error.details ?? '') as Record<string, unknown>
    if (typeof details.limit === 'number' && Number.isSafeInteger(details.limit)) limit = details.limit
    if (typeof details.usage === 'number' && Number.isSafeInteger(details.usage)) usage = details.usage
  } catch {
    // The stable code alone still identifies the refusal.
  }
  return new PlanLimitError(kind, limit, usage, kind === 'images' && usage > limit ? usage - limit : undefined)
}

/** How much more of a counted thing fits: null for unlimited. */
export function planRoom(limit: number | null, used: number): number | null {
  return limit === null ? null : Math.max(0, limit - used)
}

// The last limits the server told this device, per account, so a person who is offline (or signed in but
// not yet synced) is held to the same plan. A guest (no account) is held to the Free limits in appSettings (`guestPlan`).
/** The owner scope images use on this device before any account is signed in. */
export const GUEST_SCOPE = 'local-review'
const STORAGE_KEY = 'minimany:plan-limits:v1'
const known = new Map<string, AccountAccess['capacities']>()

function readStored(): Record<string, AccountAccess['capacities']> {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) as Record<string, AccountAccess['capacities']> : {}
  } catch {
    return {}
  }
}

export function rememberAccountLimits(ownerId: string, access: AccountAccess): void {
  if (import.meta.env?.DEV && access.accountType === 'free' && JSON.stringify(access.capacities) !== JSON.stringify(guestCapacities())) {
    console.warn('The Free plan limits from the server differ from appSettings guestPlan. Update the guest copy: docs/PLAN_LIMITS.md')
  }
  known.set(ownerId, access.capacities)
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify({ ...readStored(), [ownerId]: access.capacities }))
  } catch {
    // Storage can be unavailable; the in-memory copy still applies this session.
  }
}

/** The guest copy of the Free limits without its comment key. */
function guestCapacities(): AccountAccess['capacities'] {
  const { notes, images } = APP_SETTINGS.guestPlan
  return { notes, images }
}

export function knownAccountLimits(ownerId: string): AccountAccess['capacities'] | null {
  if (ownerId === GUEST_SCOPE) return guestCapacities()
  const memory = known.get(ownerId)
  if (memory) return memory
  const stored = readStored()[ownerId]
  if (stored && typeof stored === 'object') {
    known.set(ownerId, stored)
    return stored
  }
  return null
}

export function forgetAccountLimitsForTests(): void {
  known.clear()
  try { globalThis.localStorage?.removeItem(STORAGE_KEY) } catch { /* ignore */ }
}
